import axios from "axios";
import { api, constant } from "./config";
import { encryptDecryptJson } from "@/helpers/cryptoUtil";

const instance = axios.create({
  baseURL: `${api.api_base}${api.api_prefix_version}`,
  timeout: 20000,
  headers: {
    "content-type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

instance.interceptors.request.use(
  (reqConfig) => {
    // Handle FormData encryption (used for file uploads)
    if (reqConfig.data instanceof FormData) {
      const formData = new FormData();
      for (var pair of reqConfig.data.entries()) {
        const [key, value] = pair;
        // Encrypt non-file values, keep files as-is
        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else if (typeof value === 'string' && value) {
          formData.append(key, encryptDecryptJson(value));
        } else {
          formData.append(key, value);
        }
      }
      reqConfig.data = formData;
      return reqConfig;
    }
    
    if (reqConfig.data) {
      reqConfig.data = encryptDecryptJson(reqConfig.data);
    }
    if (reqConfig.params) {
      reqConfig.params = encryptDecryptJson(reqConfig.params);
    }

    return reqConfig;
  },
  (err) => {
    console.error("auth err", err);
  },
);

instance.interceptors.response.use(
  (response) => {
    if (response.data.data) {
      // Create a copy of the data to avoid modifying the original during iteration
      const dataCopy = { ...response.data.data };
      
      // Process each field, but skip decryption for tokens
      for (const key in dataCopy) {
        if (dataCopy.hasOwnProperty(key)) {
          // Skip decryption for token fields
          if (key.includes('Token') || key.includes('token')) {
            // Keep token values as-is (no decryption)
            continue;
          }
          // Decrypt other fields
          dataCopy[key] = encryptDecryptJson(dataCopy[key], constant.decrypt);
        }
      }
      
      response.data.data = dataCopy;
    }
    
    // Handle userReports array (for report reasons endpoint)
    if (response.data.userReports) {
      response.data.userReports = encryptDecryptJson(response.data.userReports, constant.decrypt);
    }
    
    // Handle pagination object decryption
    if (response.data.pagination) {
      response.data.pagination = encryptDecryptJson(response.data.pagination, constant.decrypt);
    }
    
    return response;
  },
  async (error) => {
    // Decrypt error response data if it exists
    if (error.response && error.response.data) {
      try {
        if (error.response.data.data) {
          // Create a copy of the data to avoid modifying the original during iteration
          const dataCopy = { ...error.response.data.data };
          
          // Process each field, but skip decryption for tokens
          for (const key in dataCopy) {
            if (dataCopy.hasOwnProperty(key)) {
              // Skip decryption for token fields
              if (key.includes('Token') || key.includes('token')) {
                // Keep token values as-is (no decryption)
                continue;
              }
              // Decrypt other fields
              dataCopy[key] = encryptDecryptJson(dataCopy[key], constant.decrypt);
            }
          }
          
          error.response.data.data = dataCopy;
        }
      } catch (decryptError) {
        console.error("Error decrypting error response:", decryptError);
      }
    }
    
    // Only redirect if NOT a login attempt (to allow error message to display).
    // The backend answers a missing/expired/revoked/invalid token with 403 + code AUTH_SESSION_INVALID
    // (a bare 403 can also mean e.g. "subscription required", so the code is checked, not the status).
    const isLoginAttempt = error.config?.url?.includes("/auth/login");
    const isSessionInvalid =
      error.response?.status === 401 ||
      (error.response?.status === 403 && error.response?.data?.code === "AUTH_SESSION_INVALID");

    if (isSessionInvalid && !isLoginAttempt) {
      // Dynamically import store to prevent circular dependency
      const { store } = await import("@/store");
      const { logoutSuccess } = await import("@/store/features/userSlice");

      store.dispatch(logoutSuccess()); // Dispatch logout
      localStorage.setItem("session_timeout", true);
      window.location.href = "/sign-in"; // Redirect to login page
    }
    return Promise.reject(error);
  },
);

export const setAuthToken = (token) => {
  if (token) {
    instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common["Authorization"];
  }
};

export default instance;
